import { newDateFromUTC } from '@bc-library/common'

// What still points at a leaver once they leave (apps/api/api/resource/departureChecklist.js). Business dates
// (departure, job rows, leave of absence, payment of a variable) are stored at UTC midnight and read back as
// the same local day; a leave day and an interview are moments.

type TPerson = {
  resourceId: string
  firstname: string
  lastname: string
  picture: string | null
}

export type TDepartureManagedJob = {
  jobId: string
  startDate: Date
  endDate: Date | null
  position: { display: string } | null
  office: { display: string } | null
  slots: number[]
  newRow: boolean
}

export type TDepartureManagedResource = TPerson & { jobs: TDepartureManagedJob[] }
export type TDepartureAssistedResource = TPerson & { isMain: boolean }

export type TDepartureLeaveOfAbsence = {
  jobId: string
  action: 'close' | 'remove'
  startDateLOA: Date
  endDateLOA: Date | null
}

export type TDepartureLeave = {
  _id: string
  leaveType: { display: string } | null
  firstDate: Date | null
  lastDate: Date | null
  daysAfter: number
  action: 'shorten' | 'delete'
}

export type TDepartureMission = {
  engagementId: string
  display: string
  projectCode: string | null
  isLocked: boolean
  daysAfter: number
}

export type TDepartureExpenseReport = {
  _id: string
  display: string
  status: string
  expenseCount: number
}

export type TDeparturePay = {
  salaryMonths: Array<{ year: number, month: number, amount: number }>
  variables: Array<{ year: number, category: string | null, amount: number, cashDate: Date }>
}

export type TDepartureInterview = {
  processId: string
  interviewId: string
  interviewDate: Date
  candidate: { firstname: string, lastname: string } | null
}

export type TDepartureCompany = {
  _id: string
  display: string
  // The owner slots the leaver holds: 1 primary, 2 secondary
  slots: number[]
}

export type TDepartureChecklist = {
  departureDate: Date | null
  departurePassed?: boolean
  leaveOfAbsence?: TDepartureLeaveOfAbsence | null
  managedResources?: TDepartureManagedResource[]
  assistedResources?: TDepartureAssistedResource[]
  leavesAfterDeparture?: TDepartureLeave[]
  staffing?: TDepartureMission[]
  timesheetActivities?: Array<{ _id: string, display: string, soleTarget: boolean }>
  expenseReports?: TDepartureExpenseReport[]
  // null without the right to see the wages
  pay?: TDeparturePay | null
  recruitment?: {
    authoredOffers: Array<{ _id: string, title: string }>
    interviewerOffers: Array<{ _id: string, title: string }>
    bookingPlans: number
    interviews: TDepartureInterview[]
  }
  companies?: TDepartureCompany[]
}

/** A person kept as they are in a handover, instead of a replacement or none */
export const DEPARTURE_KEEP = 'keep'
export type TDepartureOverride = string | null | typeof DEPARTURE_KEEP

export type TDepartureChoices = {
  leaveOfAbsence?: { apply: boolean, endDate?: Date | null }
  leavesAfterDeparture?: boolean
  staffing?: boolean
  pay?: boolean
  managedResources?: { apply: boolean, replacementId: string | null, overrides: Record<string, TDepartureOverride> }
  assistedResources?: { apply: boolean, replacementId: string | null, overrides: Record<string, TDepartureOverride> }
  recruitment?: { authorId: string | null, interviewerId: string | null }
  companies?: { apply: boolean, replacementId: string | null }
}

export type TDepartureApplyResult = {
  results: Record<string, any>
  errors: Array<{ section: string, message: string }>
}

const fromUTC = (value?: string | Date | null) => (value ? newDateFromUTC(value) : null)
const moment = (value?: string | Date | null) => (value ? new Date(value) : null)

export const departureChecklistFromApi = (data: TDepartureChecklist): TDepartureChecklist => ({
  ...data,
  departureDate: fromUTC(data.departureDate),
  leaveOfAbsence: data.leaveOfAbsence
    ? { ...data.leaveOfAbsence, startDateLOA: fromUTC(data.leaveOfAbsence.startDateLOA), endDateLOA: fromUTC(data.leaveOfAbsence.endDateLOA) }
    : null,
  managedResources: (data.managedResources || []).map(person => ({
    ...person,
    jobs: person.jobs.map(job => ({ ...job, startDate: fromUTC(job.startDate), endDate: fromUTC(job.endDate) })),
  })),
  leavesAfterDeparture: (data.leavesAfterDeparture || []).map(leave => ({
    ...leave,
    firstDate: moment(leave.firstDate),
    lastDate: moment(leave.lastDate),
  })),
  pay: data.pay
    ? { ...data.pay, variables: data.pay.variables.map(variable => ({ ...variable, cashDate: fromUTC(variable.cashDate) })) }
    : data.pay,
  recruitment: data.recruitment
    ? { ...data.recruitment, interviews: data.recruitment.interviews.map(interview => ({ ...interview, interviewDate: moment(interview.interviewDate) })) }
    : data.recruitment,
})
